import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Calendar, ArrowLeft, ArrowRight } from "lucide-react";
import Header from "./header";
import { trackSignupClick, trackFeaturesClick, trackNewsView } from "@/utils/analytics";

// 静的なお知らせデータ
const newsData: Record<string, {
  id: number;
  title: string;
  date: string;
  category: string;
  badge: string;
  badgeColor: string;
  content: string;
}> = {
  "design-settings-release": {
    id: 4,
    title: "デザイン設定機能をリリースしました",
    date: "2026/01/15",
    category: "機能追加",
    badge: "NEW",
    badgeColor: "bg-red-100 text-red-800",
    content: `
      <p class="mb-4">ダッシュボードからサイトのカラー・ロゴ・デザインスタイルをカスタマイズできる「デザイン設定」機能をリリースしました。</p>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">デザイン設定機能とは</h2>
      <p class="mb-4">
        お店のブランドイメージに合わせて、オリパサイトの見た目を自由にカスタマイズできる機能です。
        コードの知識がなくても、ダッシュボードから簡単に設定できます。
      </p>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">設定できる項目</h2>
      <ul class="list-disc list-inside mb-4 space-y-2">
        <li><strong>プライマリカラー</strong>：ボタンやアクセントに使用される主要カラー</li>
        <li><strong>セカンダリーカラー</strong>：リンクやハイライトに使用されるサブカラー</li>
        <li><strong>背景カラー</strong>：ページ全体の背景色</li>
        <li><strong>ナビゲーションカラー</strong>：ヘッダー・フッターの背景色</li>
        <li><strong>ロゴ設定</strong>：テキストロゴまたは画像ロゴを選択可能</li>
        <li><strong>デザインスタイル</strong>：ネオブルータリズム / モダン の2種類から選択</li>
      </ul>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">使い方</h2>
      <p class="mb-4">
        ダッシュボードの「デザイン設定」メニューからアクセスできます。
        カラーピッカーで色を選択し、「保存」ボタンをクリックするだけで
        すぐにお店のサイトに反映されます。
      </p>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">ボタン文字色の自動調整</h2>
      <p class="mb-4">
        プライマリカラーに暗い色を設定した場合、ボタン内の文字色は自動的に白色に調整されます。
        視認性を確保しながら、好みのカラーを自由に設定していただけます。
      </p>

      <p class="mb-4">
        ぜひデザイン設定機能をご活用いただき、お店らしいオリパサイトを作り上げてください。
      </p>
    `
  },
  "friend-referral-release": {
    id: 3,
    title: "友だち招待機能をリリースしました",
    date: "2026/01/12",
    category: "機能追加",
    badge: "",
    badgeColor: "bg-gray-100 text-gray-800",
    content: `
      <p class="mb-4">友だちを招待してポイントを獲得できる「友だち招待」機能をリリースしました。</p>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">友だち招待機能とは</h2>
      <p class="mb-4">
        ユーザーが友だちを招待し、その友だちが会員登録を完了すると、
        紹介者・被紹介者の両方にポイントが付与される機能です。
        口コミでの顧客獲得を促進し、売上向上に貢献します。
      </p>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">主な特徴</h2>
      <ul class="list-disc list-inside mb-4 space-y-2">
        <li>紹介者・被紹介者の両方にポイントを付与</li>
        <li>付与ポイント数は店舗ごとにカスタマイズ可能</li>
        <li>招待リンクをSNSやメッセージで簡単にシェア</li>
        <li>招待状況・成果をダッシュボードで確認可能</li>
      </ul>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">店舗様向け設定</h2>
      <p class="mb-4">
        ダッシュボードの「マーケティング」メニューから友だち招待機能の有効/無効を切り替えられます。
        キャンペーンを作成して、付与ポイント数や期間を自由に設定できます。
      </p>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">ユーザー様向け</h2>
      <p class="mb-4">
        マイページから招待リンクを取得し、友だちにシェアするだけで招待が完了します。
        友だちが会員登録を完了すると、自動的にポイントが付与されます。
      </p>

      <p class="mb-4">
        ぜひ友だち招待機能をご活用いただき、お店のファンを増やしてください。
      </p>
    `
  },
  "joybase-beta-release": {
    id: 1,
    title: "JOYBASEベータ版がリリースされました",
    date: "2025/10/31",
    category: "リリース",
    badge: "",
    badgeColor: "bg-gray-100 text-gray-800",
    content: `
      <p class="mb-4">この度、JOYBASEのベータ版をリリースいたしました。</p>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">JOYBASEとは</h2>
      <p class="mb-4">
        JOYBASEは、カードゲーム店舗様向けのオリジナルパック（オリパ）販売プラットフォームです。
        初期費用0円、月額費用0円で、誰でも簡単にオリパサイトを始めることができます。
      </p>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">主な機能</h2>
      <ul class="list-disc list-inside mb-4 space-y-2">
        <li>簡単なイベント作成・管理機能</li>
        <li>自動抽選システム</li>
        <li>配送管理機能</li>
        <li>売上管理・分配システム</li>
        <li>カスタマイズ可能なストアデザイン</li>
      </ul>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">ベータ版について</h2>
      <p class="mb-4">
        現在、ベータ版として運用しており、機能の追加や改善を継続的に行っております。
        ご利用いただく中でお気づきの点がございましたら、お気軽にお問い合わせください。
      </p>

      <p class="mb-4">
        皆様のご意見を参考に、より使いやすいサービスを目指してまいります。
      </p>
    `
  }
};

// サイドバー用のニュース一覧（現在の記事を除く）
const getRecentNews = (currentSlug: string | undefined) => {
  return Object.entries(newsData)
    .filter(([slug]) => slug !== currentSlug)
    .map(([slug, news]) => ({ slug, ...news }))
    .sort((a, b) => b.id - a.id)
    .slice(0, 5);
};

const NewsDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const news = slug ? newsData[slug] : null;
  const recentNews = getRecentNews(slug);

  // ページ遷移時にスクロール位置をトップに戻す
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // ニュース閲覧トラッキング
  useEffect(() => {
    if (news && slug) {
      trackNewsView(slug, news.title);
    }
  }, [slug, news]);

  // 店舗登録CTAクリックハンドラー
  const handleSignupClick = (source: string) => {
    trackSignupClick(source);
  };

  // 機能ページリンククリックハンドラー
  const handleFeaturesClick = (source: string) => {
    trackFeaturesClick(source);
  };

  if (!news) {
    return (
      <>
        <Helmet>
          <title>お知らせが見つかりません | JOYBASE</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <div className="pt-20">
            <main className="container mx-auto max-w-4xl px-4 py-8">
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">お知らせが見つかりません</h1>
                <p className="text-gray-600 mb-6">お探しのお知らせは存在しないか、削除された可能性があります。</p>
                <a
                  href="/news"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400 text-black rounded-lg font-semibold hover:bg-yellow-500 transition"
                >
                  <ArrowLeft size={20} />
                  お知らせ一覧に戻る
                </a>
              </div>
            </main>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{news.title} | JOYBASE</title>
        <meta name="description" content={news.title} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        <div className="pt-20">
          <main className="container mx-auto max-w-6xl px-4 py-8">
            {/* 戻るボタン */}
            <a
              href="/news"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
            >
              <ArrowLeft size={20} />
              お知らせ一覧に戻る
            </a>

            {/* 2カラムレイアウト */}
            <div className="flex flex-col lg:flex-row gap-8">
              {/* メインコンテンツ */}
              <div className="flex-1 min-w-0">
                {/* 記事 */}
                <article className="bg-white rounded-lg shadow-sm p-8">
                  {/* ヘッダー */}
                  <div className="mb-6 pb-6 border-b border-gray-200">
                    <div className="flex items-center gap-3 mb-4">
                      {news.badge && (
                        <span className={`inline-block px-3 py-1 ${news.badgeColor} text-xs font-semibold rounded`}>
                          {news.badge}
                        </span>
                      )}
                      <span className="text-sm text-gray-500">{news.category}</span>
                    </div>

                    <h1 className="text-3xl font-bold text-gray-900 mb-4">{news.title}</h1>

                    <div className="flex items-center gap-2 text-gray-500">
                      <Calendar size={16} />
                      <time>{news.date}</time>
                    </div>
                  </div>

                  {/* 本文 */}
                  <div
                    className="prose prose-gray max-w-none"
                    dangerouslySetInnerHTML={{ __html: news.content }}
                  />
                </article>

                {/* 店舗登録CTA（モバイル・メインコンテンツ下部） */}
                <div className="mt-8 lg:hidden bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-2xl p-6 text-center shadow-lg">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    JOYBASEでオリパサイトを始めませんか？
                  </h2>
                  <p className="text-gray-800 mb-4 text-sm">
                    初期費用0円・月額費用0円で、すぐにオリパ販売を開始できます。
                  </p>
                  <a
                    href="https://dashboard.joybase.jp/signup"
                    onClick={() => handleSignupClick('news_mobile_cta')}
                    className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition shadow-md"
                  >
                    店舗登録する
                    <ArrowRight size={18} />
                  </a>
                </div>

                {/* 戻るボタン（下部） */}
                <div className="mt-8 text-center">
                  <a
                    href="/news"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition"
                  >
                    <ArrowLeft size={20} />
                    お知らせ一覧に戻る
                  </a>
                </div>
              </div>

              {/* サイドバー */}
              <aside className="w-full lg:w-80 flex-shrink-0">
                {/* 店舗登録CTA */}
                <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl p-6 shadow-lg mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    オリパサイトを始めませんか？
                  </h3>
                  <p className="text-gray-800 text-sm mb-4">
                    初期費用0円・月額費用0円で今すぐ開始
                  </p>
                  <a
                    href="https://dashboard.joybase.jp/signup"
                    onClick={() => handleSignupClick('news_sidebar_cta')}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition shadow-md text-sm"
                  >
                    無料で店舗登録
                    <ArrowRight size={16} />
                  </a>
                  <a
                    href="/features"
                    onClick={() => handleFeaturesClick('news_sidebar')}
                    className="flex items-center justify-center gap-2 w-full mt-3 px-4 py-2 text-gray-900 font-medium hover:underline text-sm"
                  >
                    機能を詳しく見る
                  </a>
                </div>

                {/* 直近のニュース */}
                {recentNews.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm p-5">
                    <h3 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                      最新のお知らせ
                    </h3>
                    <ul className="space-y-4">
                      {recentNews.map((item) => (
                        <li key={item.slug}>
                          <a
                            href={`/news/${item.slug}`}
                            className="block group"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {item.badge && (
                                <span className={`inline-block px-2 py-0.5 ${item.badgeColor} text-xs font-semibold rounded`}>
                                  {item.badge}
                                </span>
                              )}
                              <span className="text-xs text-gray-400">{item.date}</span>
                            </div>
                            <p className="text-sm text-gray-700 group-hover:text-yellow-600 transition line-clamp-2">
                              {item.title}
                            </p>
                          </a>
                        </li>
                      ))}
                    </ul>
                    <a
                      href="/news"
                      className="flex items-center justify-center gap-1 mt-4 pt-3 border-t border-gray-100 text-sm text-gray-600 hover:text-gray-900 transition"
                    >
                      すべてのお知らせを見る
                      <ArrowRight size={14} />
                    </a>
                  </div>
                )}
              </aside>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default NewsDetailPage;
